import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { AccountDeactivatedPage, AccountPendingPage } from './AccountStatePages';
import { IdentityNotLinkedPage } from './IdentityNotLinkedPage';
import { NoPermissionsPage } from './NoPermissionsPage';
import { SessionConflictPage } from './SessionConflictPage';
import { IDENTITY_NOT_LINKED_PATH, SESSION_CONFLICT_PATH } from './interstitialPaths';
import { hasAnyPermission } from './permissions';
import { useAuth } from './useAuth';

/**
 * Gates the app shell on `useMe()` resolution.
 *
 * - `idle`            → centered loading while the first `/me` probe is in flight.
 * - `authenticated` + `status: PENDING_ACTIVATION` → the branded
 *   `AccountPendingPage` (PTV2-1424). A JIT-provisioned account awaiting
 *   verification; the backend refuses every non-`/me` path with a 403.
 * - `authenticated` + `status: DEACTIVATED` → the branded
 *   `AccountDeactivatedPage` (PTV2-1424).
 * - `authenticated` + effective permissions → render children (the app).
 * - `authenticated` + EMPTY effective permission set → render the branded
 *   `NoPermissionsPage` (PTV2-1413). A user can now be provisioned with zero
 *   roles; `/me` still answers but nothing requiring `view` is permitted. This
 *   gate sits above the router and the feature-flag probe (see `main.tsx`), so
 *   it short-circuits before any route data-loader or `view`-requiring query
 *   can fire — the only request in flight is the `/me` profile fetch.
 * - `unauthenticated` → render a sign-in screen and stop the tree from
 *   mounting. This avoids the request loop you'd otherwise get when `/me`
 *   errors with something other than a 401 (CORS / network / 403) and
 *   `handleUnauthorized()` therefore never fires. In mock mode this state
 *   only happens after `signOut()`, so we render the same screen.
 */
export function SessionGuard({ children }: { readonly children: ReactNode }): ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();

  // Public interstitial: the gateway redirects here when the signed-in identity can't access this
  // host. The user has no session here, so it renders above the auth gate (the /me probe still runs,
  // but its result is ignored on this path).
  if (window.location.pathname.endsWith(SESSION_CONFLICT_PATH)) {
    return <SessionConflictPage />;
  }

  // Public interstitial (PTV2-1448): the gateway redirects here when the signed-in external identity is
  // not linked to any user (captured PENDING for an admin to link). No session here either.
  if (window.location.pathname.endsWith(IDENTITY_NOT_LINKED_PATH)) {
    return <IdentityNotLinkedPage />;
  }

  if (auth.status === 'idle') {
    return (
      <main
        role="status"
        aria-live="polite"
        className={cn(
          'mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center gap-6 p-8',
          'text-foreground',
        )}
      >
        <p className="text-sm text-muted-foreground">{t('app.loading')}</p>
      </main>
    );
  }

  if (auth.status === 'unauthenticated') {
    return (
      <main
        role="alert"
        className="flex min-h-screen items-center justify-center bg-muted/30 p-6"
      >
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle>{t('auth.signedOut.heading')}</CardTitle>
            <CardDescription>{t('auth.signedOut.message')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              className="w-full"
              onClick={() => {
                auth.signIn();
              }}
            >
              {t('auth.signedOut.signIn')}
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  // Account-status gate (PTV2-1424, DESIGN §9.2). `status` comes from the same
  // `/me` probe already resolved above, so a non-ACTIVE user is short-circuited
  // here — above the router and the feature-flag probe — before any route
  // data-loader or `view`-requiring query can fire. The backend
  // `AccountStatusFilter` refuses every non-`/me` path for these users anyway;
  // this renders the branded explanation instead of a broken shell.
  if (auth.user?.status === 'PENDING_ACTIVATION') {
    return <AccountPendingPage />;
  }
  if (auth.user?.status === 'DEACTIVATED') {
    return <AccountDeactivatedPage />;
  }

  if (!hasAnyPermission(auth.user)) {
    return <NoPermissionsPage />;
  }

  return <>{children}</>;
}
